import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  InputAdornment,
  Autocomplete,
  useTheme as useMuiTheme,
  alpha,
  FormControlLabel,
  Checkbox,
  ListSubheader,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  Timeline as TimelineIcon,
  Person as PersonIcon,
  Groups as GroupsIcon,
  CalendarToday as CalendarIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  AttachMoney as MoneyIcon,
  Construction as ConstructionIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  TryRounded,
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { hasCapability, CAP } from '../utils/permissions';
import { useTheme as useThemeContext } from '../contexts/ThemeContext';
import projectsService, { ProjectTemplate } from '../services/projectsService';
import usersService, { Department } from '../services/usersService';
import { CreateProjectForm, WorkPackage, WorkUnit, User, ProjectStatus, ProjectContractType, Client, ClientPoc, Project, ProjectType, Party, Office } from '../types';
import LoadingAnimation from '../components/LoadingAnimation';
import AccessDenied from '../components/AccessDenied';
import { useFormFieldErrors } from '../hooks/useFormFieldErrors';
import FormValidationAlert from '../components/FormValidationAlert';
import { labelsForKeys } from '../utils/formValidation';
import ContactKindChip from '../components/ContactKindChip';
import { getZoomCompensatedAutocompleteSlotProps } from '../utils/zoomCompensation';
import {
  NdTeamOption,
  buildNdTeamOptions,
  hasNdTeamAssignment,
  ndTeamOptionKey,
  selectedNdTeamOptions,
  splitNdTeamSelection,
} from '../utils/projectTeam';
import {
  clientPocsWithEmail,
  formatClientPocLabel,
  normalizePocEmail,
  pocSelectionEmail,
} from '../utils/clientPocs';
import {
  contactKind,
  groupContactOption,
  sortContactsForPicker,
} from '../utils/contactPicker';
import { PROJECT_TYPE_PROJECT, PROJECT_TYPE_PROPOSAL } from '../utils/projectTypes';
import {
  isLeadProjectCreateState,
  isProposalTypeLockState,
  officeCodeFromId,
} from '../utils/leadProjectCreate';
import opportunitiesService from '../services/opportunitiesService';
import {
  WP0_TIMESHEET_LOCK_MESSAGE,
  hasWp0,
  isWp0,
  isWp0LockedByTimesheet,
  withWp0,
} from '../utils/workPackages';

const ProjectCreationPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId?: string }>();
  const theme = useMuiTheme();
  const { mode } = useThemeContext();
  const leadCreateState = isLeadProjectCreateState(location.state) ? location.state : null;
  const leadIntent = leadCreateState?.intent;
  const lockToProposalType = leadIntent === 'proposal' || isProposalTypeLockState(location.state);

  // Design System: Nautilus Console
  const consoleColors = {
    deepOcean: '#0f2134',
    abyssal: '#0a1622',
    sand: '#ccba9b',
    sandDim: 'rgba(204, 186, 155, 0.5)',
    text: '#ffffff',
    textDim: 'rgba(255, 255, 255, 0.7)',
  };

  // Determine if we're in update mode
  const isUpdateMode = !!projectId;

  // State management
  const [users, setUsers] = useState<User[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [statuses, setStatuses] = useState<ProjectStatus[]>([]);
  const [contractTypes, setContractTypes] = useState<ProjectContractType[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projectTypes, setProjectTypes] = useState<ProjectType[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error',
  });
  const fieldErrors = useFormFieldErrors();
  const [existingProject, setExistingProject] = useState<Project | null>(null);

  // Form states
  const [projectForm, setProjectForm] = useState<CreateProjectForm>({
    name: '',
    nd_number: '',
    pm_id: '',
    engineer_ids: [],
    department_ids: [],
    party_ids: [],
    year: new Date().getFullYear(),
    initial_budget: undefined,
    sub_contracted_budget: undefined,
    nd_budget: undefined,
    manhours: undefined,
    reference_contract: '',
    reference_nd_offer: '',
    project_start_date: '',
    project_planned_end_date: '',
    project_revised_end_date: '',
    status_id: undefined,
    contract_type_id: undefined,
    office: undefined,
    client_id: undefined,
    project_type_id: undefined,
    poc_emails: [],
    workPackages: [],
  });
  const [suggestedId, setSuggestedId] = useState<number | null>(null);
  const [isNdNumberAuto, setIsNdNumberAuto] = useState(!isUpdateMode);
  /** When creating a project, optionally add WP/WU codes GEN/GEN (previously always created server-side). */
  const [includeDefaultGenStructure, setIncludeDefaultGenStructure] = useState(true);

  // UI states for editing
  const [editingWP, setEditingWP] = useState<number | null>(null);
  const [editingWU, setEditingWU] = useState<{ wpId: number; wuId: number } | null>(null);
  const [tempWPLabel, setTempWPLabel] = useState('');
  const [tempWULabel, setTempWULabel] = useState('');
  const [pendingTemplateFiles, setPendingTemplateFiles] = useState<File[]>([]);
  const [existingTemplates, setExistingTemplates] = useState<ProjectTemplate[]>([]);
  const [uploadingTemplates, setUploadingTemplates] = useState(false);
  const [templateDragActive, setTemplateDragActive] = useState(false);
  const appliedLeadPrefillRef = useRef(false);
  const appliedProposalTypeLockRef = useRef(false);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, [projectId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [usersData, statusesData, contractTypesData, officesData, clientsData, projectTypesData, partiesData, departmentsData] = await Promise.all([
        projectsService.getUsers(),
        projectsService.getProjectStatuses(),
        projectsService.getProjectContractTypes(),
        projectsService.getOffices(),
        projectsService.getClients(),
        projectsService.getProjectTypes(),
        projectsService.getParties(),
        usersService.getDepartments(),
      ]);
      setUsers(usersData);
      setStatuses(statusesData);
      setContractTypes(contractTypesData);
      setOffices(officesData);
      setClients(clientsData);
      setProjectTypes(projectTypesData);
      setParties(partiesData);
      setDepartments(departmentsData);

      if (!isUpdateMode && projectForm.project_type_id) {
        try {
          const nextIdData = await projectsService.getNextSuggestedId(
            projectForm.project_type_id,
            projectForm.year
          );
          setSuggestedId(nextIdData.next_id);
        } catch (idErr) {
          console.error('Error fetching next project ID:', idErr);
        }
      }

      // Load project data if in update mode
      if (isUpdateMode && projectId) {
        await loadProjectData(projectId);
      }

      setError(null);
    } catch (err) {
      setError('Failed to load initial data');
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectData = async (id: string) => {
    try {
      // Get detailed project info including work packages
      const projectDetails = await projectsService.getProjectDetails(parseInt(id));

      // Get work packages and work units
      const workPackages = await projectsService.getWorkPackages(parseInt(id));
      const workUnitLists = await Promise.all(
        workPackages.map((wp) => projectsService.getWorkUnits(parseInt(id), wp.id))
      );
      let nextWpId = 1;
      const workPackagesWithUnits: WorkPackage[] = workPackages.map((wp, index) => {
        const zero = isWp0(wp.code);
        return {
          id: zero ? 0 : nextWpId++,
          code: wp.code,
          title: wp.title,
          has_timesheet_entries: Boolean(wp.has_timesheet_entries),
          workUnits: workUnitLists[index].map((wu, wuIndex) => ({
            id: wuIndex + 1,
            code: wu.code,
            title: wu.title,
          })),
        };
      });

      setExistingProject(projectDetails.project);

      try {
        const templatesData = await projectsService.getProjectTemplates(parseInt(id));
        setExistingTemplates(templatesData.templates || []);
      } catch (templateErr) {
        console.error('Error loading project templates:', templateErr);
        setExistingTemplates([]);
      }

      setProjectForm({
        name: projectDetails.project.name,
        nd_number: projectDetails.project.nd_number || '',
        pm_id: projectDetails.project.pm_id?.toString() || '',
        engineer_ids: [...new Set(projectDetails.project.engineers?.map(e => e.id.toString()) || [])],
        department_ids: [...new Set(projectDetails.project.departments?.map(d => d.id.toString()) || [])],
        party_ids: [...new Set(projectDetails.project.parties?.map(p => p.id.toString()) || [])],
        year: projectDetails.project.year || new Date().getFullYear(),
        initial_budget: projectDetails.project.initial_budget,
        sub_contracted_budget: projectDetails.project.sub_contracted_budget,
        nd_budget: projectDetails.project.nd_budget,
        manhours: projectDetails.project.manhours,
        reference_contract: projectDetails.project.reference_contract || '',
        reference_nd_offer: projectDetails.project.reference_nd_offer || '',
        project_start_date: projectDetails.project.project_start_date || '',
        project_planned_end_date: projectDetails.project.project_planned_end_date || '',
        project_revised_end_date: projectDetails.project.project_revised_end_date || '',
        status_id: projectDetails.project.status_id,
        contract_type_id: projectDetails.project.contract_type_id,
        office: projectDetails.project.office,
        client_id: projectDetails.project.client_id,
        project_type_id: projectDetails.project.project_type_id,
        poc_emails: projectDetails.project.poc_emails || [],
        workPackages: workPackagesWithUnits,
      });
      setIsNdNumberAuto(false);
    } catch (err) {
      console.error('Error loading project data:', err);
      setError('Failed to load project data');
    }
  };

  // Re-fetch counter when project type or year changes
  useEffect(() => {
    if (!isUpdateMode && projectForm.project_type_id) {
      projectsService.getNextSuggestedId(projectForm.project_type_id, projectForm.year)
        .then(data => setSuggestedId(data.next_id))
        .catch(err => console.error('Error fetching next project ID:', err));
    }
  }, [projectForm.project_type_id, projectForm.year, isUpdateMode]);

  const formatSuggestedId = (id: number): string => id.toString().padStart(3, '0');

  const ndTeamOptions = useMemo(
    () => buildNdTeamOptions(users, departments, projectForm.pm_id),
    [users, departments, projectForm.pm_id],
  );
  const selectedNdTeam = useMemo(
    () => selectedNdTeamOptions(ndTeamOptions, projectForm.engineer_ids, projectForm.department_ids || []),
    [ndTeamOptions, projectForm.engineer_ids, projectForm.department_ids],
  );
  const isProposalProject = useMemo(() => {
    const projectType = projectTypes.find((pt) => pt.id === projectForm.project_type_id);
    return projectType?.project_type === PROJECT_TYPE_PROPOSAL;
  }, [projectTypes, projectForm.project_type_id]);

  const selectableStatuses = useMemo(() => {
    if (isProposalProject) return statuses;
    return statuses.filter(
      (status) => status.status !== 'Other' || status.id === projectForm.status_id,
    );
  }, [isProposalProject, projectForm.status_id, statuses]);

  const clientOptions = useMemo(() => {
    const filtered = isProposalProject
      ? clients
      : clients.filter((client) => contactKind(client) === 'client');
    return sortContactsForPicker(filtered);
  }, [clients, isProposalProject]);

  const selectableProjectTypes = useMemo(() => {
    if (isUpdateMode) return projectTypes;
    if (lockToProposalType) {
      return projectTypes.filter((pt) => pt.project_type === PROJECT_TYPE_PROPOSAL);
    }
    if (leadIntent === 'awarded') {
      return projectTypes.filter((pt) => pt.project_type === PROJECT_TYPE_PROJECT);
    }
    return projectTypes.filter((pt) => pt.project_type !== PROJECT_TYPE_PROPOSAL);
  }, [isUpdateMode, leadIntent, lockToProposalType, projectTypes]);

  useEffect(() => {
    if (isUpdateMode || appliedLeadPrefillRef.current || !leadCreateState) return;
    if (projectTypes.length === 0) return;
    if (leadCreateState.office_id && offices.length === 0) return;
    const typeCode = leadCreateState.intent === 'proposal' ? PROJECT_TYPE_PROPOSAL : PROJECT_TYPE_PROJECT;
    const type = projectTypes.find((pt) => pt.project_type === typeCode);
    appliedLeadPrefillRef.current = true;
    setProjectForm((prev) => ({
      ...prev,
      name: (leadCreateState.name || '').toUpperCase(),
      client_id: leadCreateState.client_id,
      office: officeCodeFromId(offices, leadCreateState.office_id) || prev.office,
      pm_id: leadCreateState.commercial_owner_id ? String(leadCreateState.commercial_owner_id) : prev.pm_id,
      poc_emails: leadCreateState.email ? [leadCreateState.email] : prev.poc_emails,
      year: leadCreateState.year || prev.year,
      initial_budget: leadCreateState.initial_budget ?? prev.initial_budget,
      nd_budget: leadCreateState.nd_budget ?? prev.nd_budget,
      project_start_date: leadCreateState.project_start_date || prev.project_start_date,
      project_planned_end_date: leadCreateState.project_planned_end_date || prev.project_planned_end_date,
      project_type_id: type?.id ?? prev.project_type_id,
    }));
  }, [isUpdateMode, leadCreateState, offices, projectTypes]);

  const fromOpportunityId = leadCreateState?.fromOpportunityId;

  useEffect(() => {
    if (isUpdateMode || leadIntent !== 'awarded' || !fromOpportunityId) return;
    let cancelled = false;
    (async () => {
      try {
        const reference = await opportunitiesService.getLatestOfferReference(fromOpportunityId);
        if (cancelled || !reference) return;
        setProjectForm((prev) => (
          prev.reference_nd_offer ? prev : { ...prev, reference_nd_offer: reference }
        ));
      } catch (err) {
        console.error('Failed to load latest offer reference', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isUpdateMode, leadIntent, fromOpportunityId]);

  useEffect(() => {
    if (isUpdateMode || leadCreateState || appliedProposalTypeLockRef.current || !lockToProposalType) return;
    if (projectTypes.length === 0) return;
    const type = projectTypes.find((pt) => pt.project_type === PROJECT_TYPE_PROPOSAL);
    if (!type) return;
    appliedProposalTypeLockRef.current = true;
    setProjectForm((prev) => ({
      ...prev,
      project_type_id: type.id,
    }));
  }, [isUpdateMode, leadCreateState, lockToProposalType, projectTypes]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === projectForm.client_id) || null,
    [clients, projectForm.client_id],
  );
  const recommendedPocs = useMemo(
    () => clientPocsWithEmail(selectedClient),
    [selectedClient],
  );
  const pocByEmail = useMemo(() => {
    const map = new Map<string, ClientPoc>();
    for (const poc of recommendedPocs) {
      if (poc.email) map.set(normalizePocEmail(poc.email), poc);
    }
    return map;
  }, [recommendedPocs]);
  const selectedPocValue = useMemo(
    () => (projectForm.poc_emails || []).map((email) => pocByEmail.get(normalizePocEmail(email)) || email),
    [projectForm.poc_emails, pocByEmail],
  );

  // Effect for auto-generating ND Number
  useEffect(() => {
    if (!isUpdateMode && isNdNumberAuto && suggestedId) {
      const client = clients.find(c => c.id === projectForm.client_id);
      const projectType = projectTypes.find(pt => pt.id === projectForm.project_type_id);

      const clientAbbr = client?.abbreviation || 'XX';
      const yearStr = projectForm.year.toString().slice(-2);
      const typeCode = projectType?.project_type || '00';

      const newNdNumber = `ND-${clientAbbr}-${yearStr}-${typeCode}${formatSuggestedId(suggestedId)}`;
      if (projectForm.nd_number !== newNdNumber) {
        setProjectForm(prev => ({ ...prev, nd_number: newNdNumber }));
      }
    }
  }, [projectForm.client_id, projectForm.project_type_id, projectForm.year, suggestedId, clients, projectTypes, isNdNumberAuto, isUpdateMode]);

  const handleAddTemplateFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setPendingTemplateFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const handleTemplateDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploadingTemplates) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setTemplateDragActive(true);
    } else if (e.type === 'dragleave') {
      setTemplateDragActive(false);
    }
  }, [uploadingTemplates]);

  const handleTemplateDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setTemplateDragActive(false);
    if (uploadingTemplates) return;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleAddTemplateFiles(e.dataTransfer.files);
    }
  }, [uploadingTemplates]);

  const handleRemovePendingTemplate = (index: number) => {
    setPendingTemplateFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDeleteExistingTemplate = async (templateId: string) => {
    if (!projectId) return;
    try {
      setUploadingTemplates(true);
      await projectsService.deleteProjectTemplate(parseInt(projectId), templateId);
      setExistingTemplates((prev) => prev.filter((t) => t.id !== templateId));
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete template');
    } finally {
      setUploadingTemplates(false);
    }
  };

  const uploadPendingTemplates = async (targetProjectId: number) => {
    if (pendingTemplateFiles.length === 0) return;
    await projectsService.uploadProjectTemplates(targetProjectId, pendingTemplateFiles);
    setPendingTemplateFiles([]);
  };

  const handleSubmitProject = async () => {
    const missing: string[] = [];
    if (!projectForm.name.trim()) missing.push('name');
    if (!hasNdTeamAssignment(projectForm.engineer_ids, projectForm.department_ids || [])) {
      missing.push('nd_team');
    }
    if (missing.length > 0) {
      fieldErrors.showMissing(missing, labelsForKeys(missing, {
        name: 'Project name',
        nd_team: 'ND team',
      }));
      return;
    }
    try {
      setLoading(true);
      fieldErrors.clear();
      if (isUpdateMode && projectId) {
        await projectsService.updateProject(parseInt(projectId), projectForm);
        if (pendingTemplateFiles.length > 0) {
          setUploadingTemplates(true);
          await uploadPendingTemplates(parseInt(projectId));
          const templatesData = await projectsService.getProjectTemplates(parseInt(projectId));
          setExistingTemplates(templatesData.templates || []);
        }
        navigate(leadCreateState ? '/sales/leads' : '/projects');
      } else {
        const hasGenWp = projectForm.workPackages.some(
          (wp) => (wp.code || '').trim().toUpperCase() === 'GEN'
        );
        let workPackages = projectForm.workPackages;
        if (includeDefaultGenStructure && !hasGenWp) {
          const nextWpId =
            workPackages.length > 0 ? Math.max(...workPackages.map((wp) => wp.id)) + 1 : 1;
          workPackages = [
            ...workPackages,
            {
              id: nextWpId,
              code: 'GEN',
              title: '',
              workUnits: [{ id: 1, code: 'GEN', title: '' }],
            },
          ];
        }
        if (leadIntent === 'awarded' && projectForm.client_id) {
          const client = clients.find((c) => c.id === projectForm.client_id);
          if (client && contactKind(client) === 'lead') {
            await projectsService.updateClient(client.id, { kind: 'client' });
          }
        }
        const result = await projectsService.createProject({ ...projectForm, workPackages });
        if (pendingTemplateFiles.length > 0) {
          setUploadingTemplates(true);
          await uploadPendingTemplates(result.project_id);
        }
        if (leadCreateState) {
          try {
            await opportunitiesService.linkProject(
              leadCreateState.fromOpportunityId,
              leadCreateState.intent,
              result.project_id,
            );
          } catch (linkErr) {
            console.error('Failed to link project to lead', linkErr);
          }
          navigate(`/projects/${result.project_id}/documents`);
        } else if (lockToProposalType) {
          navigate('/sales/proposals');
        } else {
          navigate('/projects');
        }
      }
    } catch (err: any) {
      const message = err.response?.data?.error || `Failed to ${isUpdateMode ? 'update' : 'create'} project`;
      setError(message);
      if (message === WP0_TIMESHEET_LOCK_MESSAGE) {
        setSnackbar({ open: true, message, severity: 'error' });
      }
    } finally {
      setLoading(false);
      setUploadingTemplates(false);
    }
  };

  const addWorkPackage = () => {
    // Usage of Math.max ensures we don't duplicate IDs if there are gaps (e.g. WP1, WP3 -> new is WP4)
    const newId = projectForm.workPackages.length > 0 ? Math.max(...projectForm.workPackages.map(wp => wp.id)) + 1 : 1;
    const newWP: WorkPackage = {
      id: newId,
      code: `WP${newId}`,
      title: '',
      workUnits: [],
    };
    setProjectForm({
      ...projectForm,
      workPackages: [...projectForm.workPackages, newWP],
    });
  };

  const showWp0LockToast = () => {
    setSnackbar({
      open: true,
      message: WP0_TIMESHEET_LOCK_MESSAGE,
      severity: 'error',
    });
  };

  const handleWp0Toggle = (checked: boolean) => {
    if (!checked && isWp0LockedByTimesheet(projectForm.workPackages)) {
      showWp0LockToast();
      return;
    }
    setProjectForm((prev) => ({
      ...prev,
      workPackages: withWp0(prev.workPackages, checked),
    }));
  };

  const removeWorkPackage = (wpId: number) => {
    const target = projectForm.workPackages.find((w) => w.id === wpId);
    if (target && isWp0(target.code) && target.has_timesheet_entries) {
      showWp0LockToast();
      return;
    }
    // Simply filter out the WP. Do NOT reindex codes to avoid changing identity of existing WPs (which causes data loss in backend)
    setProjectForm({
      ...projectForm,
      workPackages: projectForm.workPackages.filter(wp => wp.id !== wpId),
    });
  };

  const addWorkUnit = (wpId: number) => {
    const wp = projectForm.workPackages.find(w => w.id === wpId);
    if (!wp) return;

    // Use 1-based index from the WP id for the prefix
    const wpIndex = wp.id;
    const newId = wp.workUnits.length > 0 ? Math.max(...wp.workUnits.map(wu => wu.id)) + 1 : 1;
    const newWU: WorkUnit = {
      id: newId,
      code: `WU${wpIndex}.${newId}`,
      title: '',
    };

    setProjectForm({
      ...projectForm,
      workPackages: projectForm.workPackages.map(w =>
        w.id === wpId
          ? { ...w, workUnits: [...w.workUnits, newWU] }
          : w
      ),
    });
  };

  const removeWorkUnit = (wpId: number, wuId: number) => {
    setProjectForm({
      ...projectForm,
      workPackages: projectForm.workPackages.map(w =>
        w.id === wpId
          ? { ...w, workUnits: w.workUnits.filter(wu => wu.id !== wuId) }
          : w
      ),
    });
  };

  const startEditWP = (wpId: number) => {
    const wp = projectForm.workPackages.find(w => w.id === wpId);
    if (wp) {
      setEditingWP(wpId);
      setTempWPLabel(wp.title || '');
    }
  };

  const saveEditWP = (wpId: number) => {
    setProjectForm({
      ...projectForm,
      workPackages: projectForm.workPackages.map(w =>
        w.id === wpId ? { ...w, title: tempWPLabel } : w
      ),
    });
    setEditingWP(null);
    setTempWPLabel('');
  };

  const cancelEditWP = () => {
    setEditingWP(null);
    setTempWPLabel('');
  };

  const startEditWU = (wpId: number, wuId: number) => {
    const wp = projectForm.workPackages.find(w => w.id === wpId);
    const wu = wp?.workUnits.find(w => w.id === wuId);
    if (wu) {
      setEditingWU({ wpId, wuId });
      setTempWULabel(wu.title || '');
    }
  };

  const saveEditWU = (wpId: number, wuId: number) => {
    setProjectForm({
      ...projectForm,
      workPackages: projectForm.workPackages.map(w =>
        w.id === wpId
          ? {
            ...w,
            workUnits: w.workUnits.map(wu =>
              wu.id === wuId ? { ...wu, title: tempWULabel } : wu
            ),
          }
          : w
      ),
    });
    setEditingWU(null);
    setTempWULabel('');
  };

  const cancelEditWU = () => {
    setEditingWU(null);
    setTempWULabel('');
  };

  const canCreateProject = hasCapability(user, CAP.CREATE_PROJECT);

  if (loading && users.length === 0 && clients.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingAnimation />
      </Box>
    );
  }

  if (!canCreateProject) {
    return (
      <AccessDenied
        title="Access denied"
        message="You do not have permission to create projects."
        secondaryAction={{ label: 'My projects', to: '/projects' }}
      />
    );
  }

  return (
    <Box>
      {/* Console Header Panel */}
      <Box sx={{
        mb: 4,
        p: 3,
        backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
        border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
        borderLeft: `4px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
        boxShadow: mode === 'dark' ? `0 0 20px ${alpha(consoleColors.deepOcean, 0.5)}` : '0 2px 10px rgba(0,0,0,0.05)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <ConstructionIcon sx={{ fontSize: 24, color: mode === 'dark' ? consoleColors.sand : 'primary.main' }} />
            <Typography variant="overline" sx={{
              fontSize: '0.875rem',
              letterSpacing: '0.2em',
              fontWeight: 700,
              color: mode === 'dark' ? consoleColors.sand : 'primary.main'
            }}>
              PROJECT COMMAND
            </Typography>
          </Box>
          <Typography variant="h4" sx={{
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: mode === 'dark' ? '#fff' : '#1a1a1a',
            fontFamily: 'monospace'
          }}>
            {isUpdateMode ? `MODIFY: ${existingProject?.name || ''}` : 'INITIALIZE PROJECT'}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate(leadCreateState ? '/sales/leads' : '/projects')}
          aria-label={leadCreateState ? 'Cancel and return to leads' : 'Cancel and return to projects'}
          sx={{
            height: 48,
            px: 3,
            border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
            color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
            borderRadius: 0,
            fontWeight: 600,
            letterSpacing: '0.1em',
            '&:hover': {
              backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
            }
          }}
        >
          ABORT
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{
          mb: 3,
          borderRadius: 0,
          backgroundColor: alpha('#d32f2f', 0.1),
          color: '#d32f2f',
          border: '1px solid #d32f2f'
        }} role="alert">
          {error}
        </Alert>
      )}
      <FormValidationAlert
        message={fieldErrors.message}
        onClose={fieldErrors.clear}
        sx={{ mx: 0, mb: 3 }}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{
            borderRadius: 0,
            backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
            border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
            boxShadow: mode === 'dark' ? `0 0 20px ${alpha(consoleColors.deepOcean, 0.5)}` : '0 2px 10px rgba(0,0,0,0.05)',
            '&:hover': { transform: 'none', boxShadow: mode === 'dark' ? `0 0 20px ${alpha(consoleColors.deepOcean, 0.5)}` : '0 2px 10px rgba(0,0,0,0.05)' }
          }}>
            {/* Card Header Strip */}
            <Box sx={{
              px: 3,
              py: 2,
              borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`,
              backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f8f9fa'
            }}>
              <Typography variant="overline" sx={{
                fontWeight: 700,
                letterSpacing: '0.1em',
                fontSize: '0.875rem',
                color: mode === 'dark' ? consoleColors.sand : 'primary.main'
              }}>
                PROJECT PARAMETERS
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={selectableProjectTypes}
                    getOptionLabel={(option) => option.description}
                    value={selectableProjectTypes.find(pt => pt.id === projectForm.project_type_id) || projectTypes.find(pt => pt.id === projectForm.project_type_id) || null}
                    onChange={(_, newValue) => {
                      const newTypeId = newValue?.id;
                      setProjectForm((prev) => {
                        const next = { ...prev, project_type_id: newTypeId };
                        if (newValue?.project_type !== PROJECT_TYPE_PROPOSAL && prev.client_id) {
                          const currentClient = clients.find((c) => c.id === prev.client_id);
                          if (currentClient && contactKind(currentClient) === 'lead') {
                            next.client_id = undefined;
                            next.poc_emails = [];
                          }
                        }
                        return next;
                      });
                    }}
                    disabled={isUpdateMode || Boolean(leadIntent) || lockToProposalType}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Project Type"
                        margin="normal"
                        sx={{
                          backgroundColor: (theme) => (isUpdateMode || leadIntent || lockToProposalType) ?
                            (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)') :
                            'transparent'
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Project Name"
                    value={projectForm.name}
                    error={fieldErrors.invalid('name')}
                    onChange={(e) => {
                      fieldErrors.clearField('name');
                      setProjectForm({ ...projectForm, name: e.target.value.toUpperCase() });
                    }}
                    required
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={clientOptions}
                    getOptionLabel={(option) => option.name}
                    groupBy={isProposalProject ? (option: Client) => groupContactOption(option) : undefined}
                    value={selectedClient}
                    onChange={(_, newValue: Client | null) => setProjectForm({
                      ...projectForm,
                      client_id: newValue?.id,
                      poc_emails: [],
                    })}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderGroup={isProposalProject ? (params) => (
                      <li key={params.key}>
                        {params.group ? (
                          <ListSubheader
                            component="div"
                            sx={{
                              lineHeight: '32px',
                              fontSize: '0.7rem',
                              fontWeight: 700,
                              letterSpacing: '0.06em',
                              textTransform: 'uppercase',
                              bgcolor: 'background.paper',
                            }}
                          >
                            {params.group}
                          </ListSubheader>
                        ) : null}
                        <ul style={{ padding: 0 }}>{params.children}</ul>
                      </li>
                    ) : undefined}
                    renderOption={isProposalProject ? (props, option) => (
                      <li {...props} key={option.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <ContactKindChip kind={contactKind(option)} />
                        </Box>
                      </li>
                    ) : undefined}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={isProposalProject ? 'Client or lead' : 'Client'}
                        margin="normal"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={offices}
                    getOptionLabel={(option) => {
                      const code = option.code?.trim();
                      const name = option.name?.trim();
                      if (code && name) return `${code} - ${name}`;
                      return name || code || option.country;
                    }}
                    value={offices.find(o => o.code === projectForm.office) || null}
                    onChange={(_, newValue) => setProjectForm({ ...projectForm, office: newValue?.code || undefined })}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderInput={(params) => <TextField {...params} label="Office" margin="normal" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="ND Number"
                    value={projectForm.nd_number}
                    onChange={(e) => {
                      setProjectForm({ ...projectForm, nd_number: e.target.value });
                      setIsNdNumberAuto(false);
                    }}
                    margin="normal"
                    helperText={isNdNumberAuto ? "Auto-generated suggestion" : "Manually edited"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    freeSolo
                    openOnFocus
                    disableCloseOnSelect
                    filterSelectedOptions
                    options={recommendedPocs}
                    value={selectedPocValue}
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? formatClientPocLabel(pocByEmail.get(normalizePocEmail(option)) || { full_name: '', email: option })
                        : formatClientPocLabel(option)
                    }
                    isOptionEqualToValue={(option, value) =>
                      normalizePocEmail(pocSelectionEmail(option)) === normalizePocEmail(pocSelectionEmail(value))
                    }
                    noOptionsText={
                      !selectedClient
                        ? 'Select a client to see their contacts'
                        : recommendedPocs.length === 0
                          ? 'No contacts with email for this client'
                          : 'No remaining contacts'
                    }
                    onChange={(_, newValue) => {
                      const emails: string[] = [];
                      const seen = new Set<string>();
                      for (const item of newValue) {
                        const email = pocSelectionEmail(item);
                        if (!email) continue;
                        const key = normalizePocEmail(email);
                        if (seen.has(key)) continue;
                        seen.add(key);
                        emails.push(email);
                      }
                      setProjectForm({ ...projectForm, poc_emails: emails });
                    }}
                    filterOptions={(options, { inputValue }) => {
                      const query = inputValue.trim().toLowerCase();
                      if (!query) return options;
                      return options.filter((option) => {
                        if (typeof option === 'string') {
                          return option.toLowerCase().includes(query);
                        }
                        return (
                          (option.full_name || '').toLowerCase().includes(query) ||
                          (option.email || '').toLowerCase().includes(query) ||
                          (option.job_position || '').toLowerCase().includes(query)
                        );
                      });
                    }}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderOption={(props, option) => {
                      const poc = typeof option === 'string'
                        ? { full_name: '', email: option, job_position: undefined }
                        : option;
                      return (
                        <li {...props}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.25 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {poc.full_name || poc.email}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {[poc.job_position, poc.full_name ? poc.email : null].filter(Boolean).join(' · ')}
                            </Typography>
                          </Box>
                        </li>
                      );
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        const email = pocSelectionEmail(option);
                        const poc = typeof option === 'string'
                          ? pocByEmail.get(normalizePocEmail(option))
                          : option;
                        return (
                          <Chip
                            key={key}
                            icon={poc && typeof poc !== 'string' ? <PersonIcon /> : undefined}
                            variant="outlined"
                            label={poc && typeof poc !== 'string' ? formatClientPocLabel(poc) : email}
                            {...tagProps}
                            sx={{
                              borderColor: mode === 'dark' ? alpha(consoleColors.sand, 0.5) : undefined,
                              color: mode === 'dark' ? consoleColors.sand : undefined
                            }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Point of Contact (POC)"
                        margin="normal"
                        helperText={
                          !selectedClient
                            ? 'Select a client to choose their contacts. Used as default transmittal recipients.'
                            : recommendedPocs.length > 0
                              ? `Choose one or more contacts from ${selectedClient.name}. Type another email and press Enter if needed.`
                              : `No emailed contacts saved for ${selectedClient.name}. Type an email and press Enter.`
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Year"
                    type="number"
                    value={projectForm.year}
                    onChange={(e) => setProjectForm({ ...projectForm, year: parseInt(e.target.value) || new Date().getFullYear() })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={users}
                    getOptionLabel={(option) => option.name}
                    value={users.find(u => u.id.toString() === projectForm.pm_id) || null}
                    onChange={(_, newValue) => {
                      const newPmId = newValue?.id.toString() || '';
                      setProjectForm(prev => ({
                        ...prev,
                        pm_id: newPmId,
                        engineer_ids: prev.engineer_ids.filter(id => id !== newPmId)
                      }));
                    }}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderInput={(params) => <TextField {...params} label="Project Manager" margin="normal" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Initial Budget"
                    type="number"
                    value={projectForm.initial_budget || ''}
                    onChange={(e) => setProjectForm({ ...projectForm, initial_budget: e.target.value ? parseFloat(e.target.value) : undefined })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Sub-contracted Budget"
                    type="number"
                    value={projectForm.sub_contracted_budget || ''}
                    onChange={(e) => setProjectForm({ ...projectForm, sub_contracted_budget: e.target.value ? parseFloat(e.target.value) : undefined })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="ND Budget"
                    type="number"
                    value={projectForm.nd_budget || ''}
                    onChange={(e) => setProjectForm({ ...projectForm, nd_budget: e.target.value ? parseFloat(e.target.value) : undefined })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    fullWidth
                    label="Manhours"
                    type="number"
                    value={projectForm.manhours || ''}
                    onChange={(e) => setProjectForm({ ...projectForm, manhours: e.target.value ? parseFloat(e.target.value) : undefined })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference Contract"
                    value={projectForm.reference_contract || ''}
                    onChange={(e) => setProjectForm({ ...projectForm, reference_contract: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Reference ND Offer"
                    value={projectForm.reference_nd_offer || ''}
                    onChange={(e) => setProjectForm({ ...projectForm, reference_nd_offer: e.target.value })}
                    margin="normal"
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={projectForm.project_start_date}
                    onChange={(e) => setProjectForm({ ...projectForm, project_start_date: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Planned End Date"
                    type="date"
                    value={projectForm.project_planned_end_date}
                    onChange={(e) => setProjectForm({ ...projectForm, project_planned_end_date: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Revised End Date"
                    type="date"
                    value={projectForm.project_revised_end_date}
                    onChange={(e) => setProjectForm({ ...projectForm, project_revised_end_date: e.target.value })}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={selectableStatuses}
                    getOptionLabel={(option) => option.status}
                    value={selectableStatuses.find(s => s.id === projectForm.status_id) || null}
                    onChange={(_, newValue) => setProjectForm({ ...projectForm, status_id: newValue?.id })}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderInput={(params) => <TextField {...params} label="Status" margin="normal" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={contractTypes}
                    getOptionLabel={(option) => option.type}
                    value={contractTypes.find(ct => ct.id === projectForm.contract_type_id) || null}
                    onChange={(_, newValue) => setProjectForm({ ...projectForm, contract_type_id: newValue?.id })}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderInput={(params) => <TextField {...params} label="Contract Type" margin="normal" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={ndTeamOptions}
                    groupBy={(option) => option.kind === 'department' ? 'Departments' : 'People'}
                    getOptionLabel={(option) => (
                      option.kind === 'department'
                        ? `${option.label} (department)`
                        : option.departmentName
                          ? `${option.label} · ${option.departmentName}`
                          : option.label
                    )}
                    isOptionEqualToValue={(option, value) => ndTeamOptionKey(option) === ndTeamOptionKey(value)}
                    value={selectedNdTeam}
                    onChange={(_, newValue) => {
                      fieldErrors.clearField('nd_team');
                      const next = splitNdTeamSelection(newValue as NdTeamOption[]);
                      setProjectForm({
                        ...projectForm,
                        engineer_ids: next.engineer_ids,
                        department_ids: next.department_ids,
                      });
                    }}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        const isDepartment = option.kind === 'department';
                        return (
                          <Chip
                            key={key}
                            icon={isDepartment ? <GroupsIcon /> : <PersonIcon />}
                            variant={isDepartment ? 'filled' : 'outlined'}
                            label={option.label}
                            {...tagProps}
                            sx={{
                              borderColor: mode === 'dark' ? alpha(consoleColors.sand, 0.5) : undefined,
                              color: mode === 'dark' ? consoleColors.sand : undefined,
                              backgroundColor: isDepartment
                                ? (mode === 'dark' ? alpha(consoleColors.sand, 0.18) : alpha(theme.palette.primary.main, 0.12))
                                : undefined,
                            }}
                          />
                        );
                      })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Assigned ND Team"
                        margin="normal"
                        error={fieldErrors.invalid('nd_team')}
                        helperText="Add people and/or any department. Department membership stays live as people join or leave that department."
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={parties}
                    getOptionLabel={(option) => `${option.name}`}
                    value={parties.filter(p => projectForm.party_ids.includes(p.id.toString()))}
                    onChange={(_, newValue) => {
                      setProjectForm({
                        ...projectForm,
                        party_ids: newValue.map(p => p.id.toString())
                      });
                    }}
                    slotProps={getZoomCompensatedAutocompleteSlotProps()}
                    renderInput={(params) => <TextField {...params} label="Assigned Third Parties" margin="normal" helperText="Select external parties assigned to this project" />}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{
            borderRadius: 0,
            backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
            border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
            boxShadow: mode === 'dark' ? `0 0 20px ${alpha(consoleColors.deepOcean, 0.5)}` : '0 2px 10px rgba(0,0,0,0.05)',
            '&:hover': { transform: 'none', boxShadow: mode === 'dark' ? `0 0 20px ${alpha(consoleColors.deepOcean, 0.5)}` : '0 2px 10px rgba(0,0,0,0.05)' }
          }}>
            {/* Card Header Strip */}
            <Box sx={{
              px: 3,
              py: 2,
              borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`,
              backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="overline" sx={{
                fontWeight: 700,
                letterSpacing: '0.1em',
                fontSize: '0.875rem',
                color: mode === 'dark' ? consoleColors.sand : 'primary.main'
              }}>
                WORK STRUCTURE
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addWorkPackage}
                variant="outlined"
                sx={{
                  borderRadius: 0,
                  border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                  color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  '&:hover': {
                    backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.1) : alpha(theme.palette.primary.main, 0.05),
                    border: `1px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
                  }
                }}
              >
                ADD WP
              </Button>
            </Box>
            <CardContent sx={{ p: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasWp0(projectForm.workPackages)}
                    onChange={(_, checked) => handleWp0Toggle(checked)}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    Add work package WP0
                  </Typography>
                }
                sx={{ alignItems: 'flex-start', mb: isUpdateMode ? 2 : 1, mr: 0 }}
              />
              {!isUpdateMode && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeDefaultGenStructure}
                      onChange={(_, checked) => setIncludeDefaultGenStructure(checked)}
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" color="text.secondary">
                      Add default General work package and unit (GEN / GEN) - useful for
                      uncategorized work
                    </Typography>
                  }
                  sx={{ alignItems: 'flex-start', mb: 2, mr: 0 }}
                />
              )}

              {projectForm.workPackages.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4, fontFamily: 'monospace' }}>
                  NO PACKAGES CONFIGURED
                </Typography>
              ) : (
                <Box>
                  {projectForm.workPackages.map((wp) => (
                    <Accordion key={wp.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box display="flex" alignItems="center" width="100%">
                          <FolderIcon sx={{ mr: 1, color: 'primary.main' }} />
                          {/* Read-only Code */}
                          <Typography variant="body2" sx={{ mr: 2, fontWeight: 'bold', color: 'text.secondary', minWidth: '60px' }}>
                            {wp.code}
                          </Typography>

                          {editingWP === wp.id ? (
                            <TextField
                              size="small"
                              placeholder="Title"
                              value={tempWPLabel}
                              onChange={(e) => setTempWPLabel(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              sx={{ flex: 1, mr: 1 }}
                              autoFocus
                            />
                          ) : (
                            <Typography sx={{ flex: 1 }}>
                              {wp.title || <span style={{ fontStyle: 'italic', color: '#999' }}>No Title</span>}
                            </Typography>
                          )}
                          <Box>
                            {editingWP === wp.id ? (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    saveEditWP(wp.id);
                                  }}
                                >
                                  <SaveIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    cancelEditWP();
                                  }}
                                >
                                  <CancelIcon />
                                </IconButton>
                              </>
                            ) : (
                              <>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    startEditWP(wp.id);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeWorkPackage(wp.id);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="subtitle2">Work Units</Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addWorkUnit(wp.id)}
                          >
                            Add WU
                          </Button>
                        </Box>

                        {wp.workUnits.length === 0 ? (
                          <Typography color="text.secondary" variant="body2">
                            No work units added
                          </Typography>
                        ) : (
                          <List dense>
                            {wp.workUnits.map((wu) => (
                              <ListItem key={wu.id} sx={{ px: 0 }}>
                                <TimelineIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                {/* Read-only Code */}
                                <Typography variant="caption" sx={{ mr: 2, fontWeight: 'bold', color: 'text.secondary', minWidth: '50px' }}>
                                  {wu.code}
                                </Typography>

                                {editingWU?.wpId === wp.id && editingWU?.wuId === wu.id ? (
                                  <TextField
                                    size="small"
                                    placeholder="Title"
                                    value={tempWULabel}
                                    onChange={(e) => setTempWULabel(e.target.value)}
                                    sx={{ flex: 1, mr: 1 }}
                                    autoFocus
                                  />
                                ) : (
                                  <ListItemText
                                    primary={wu.title || <span style={{ fontStyle: 'italic', color: '#999' }}>No Title</span>}
                                    sx={{ flex: 1 }}
                                  />
                                )}
                                <Box>
                                  {editingWU?.wpId === wp.id && editingWU?.wuId === wu.id ? (
                                    <>
                                      <IconButton
                                        size="small"
                                        onClick={() => saveEditWU(wp.id, wu.id)}
                                      >
                                        <SaveIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={cancelEditWU}
                                      >
                                        <CancelIcon />
                                      </IconButton>
                                    </>
                                  ) : (
                                    <>
                                      <IconButton
                                        size="small"
                                        onClick={() => startEditWU(wp.id, wu.id)}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => removeWorkUnit(wp.id, wu.id)}
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </>
                                  )}
                                </Box>
                              </ListItem>
                            ))}
                          </List>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{
        mt: 3,
        borderRadius: 0,
        backgroundColor: mode === 'dark' ? consoleColors.abyssal : '#fff',
        border: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#e0e0e0'}`,
        boxShadow: mode === 'dark' ? `0 0 20px ${alpha(consoleColors.deepOcean, 0.5)}` : '0 2px 10px rgba(0,0,0,0.05)',
      }}>
        <Box sx={{
          px: 3,
          py: 2,
          borderBottom: `1px solid ${mode === 'dark' ? alpha(consoleColors.sand, 0.2) : '#e0e0e0'}`,
          backgroundColor: mode === 'dark' ? alpha(consoleColors.deepOcean, 0.5) : '#f8f9fa',
        }}>
          <Typography variant="overline" sx={{
            fontWeight: 700,
            letterSpacing: '0.1em',
            fontSize: '0.875rem',
            color: mode === 'dark' ? consoleColors.sand : 'primary.main',
          }}>
            DEFAULT TEMPLATES
          </Typography>
        </Box>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Optional files available to all project users from the Documents page.
          </Typography>

          {existingTemplates.length > 0 && (
            <List dense sx={{ mb: 2 }}>
              {existingTemplates.map((template) => (
                <ListItem
                  key={template.id}
                  secondaryAction={
                    isUpdateMode ? (
                      <IconButton
                        edge="end"
                        color="error"
                        onClick={() => handleDeleteExistingTemplate(template.id)}
                        disabled={uploadingTemplates}
                      >
                        <DeleteIcon />
                      </IconButton>
                    ) : null
                  }
                >
                  <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <ListItemText
                    primary={template.filename}
                    secondary={`${(template.size / 1024 / 1024).toFixed(2)} MB`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          {pendingTemplateFiles.length > 0 && (
            <List dense sx={{ mb: 2 }}>
              {pendingTemplateFiles.map((file, index) => (
                <ListItem
                  key={`${file.name}-${index}`}
                  secondaryAction={
                    <IconButton edge="end" color="error" onClick={() => handleRemovePendingTemplate(index)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB — pending upload`}
                  />
                </ListItem>
              ))}
            </List>
          )}

          <Paper
            sx={{
              p: 3,
              border: '2px dashed',
              borderColor: templateDragActive
                ? (mode === 'dark' ? consoleColors.sand : 'primary.main')
                : (mode === 'dark' ? alpha(consoleColors.sand, 0.3) : 'grey.300'),
              backgroundColor: templateDragActive
                ? (mode === 'dark' ? alpha(consoleColors.sand, 0.08) : 'action.hover')
                : (mode === 'dark' ? alpha(consoleColors.deepOcean, 0.3) : 'background.paper'),
              textAlign: 'center',
              cursor: uploadingTemplates ? 'default' : 'pointer',
              opacity: uploadingTemplates ? 0.6 : 1,
              transition: 'all 0.3s ease',
              borderRadius: 0,
              ...(!uploadingTemplates && {
                '&:hover': {
                  borderColor: mode === 'dark' ? consoleColors.sand : 'primary.main',
                  backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.08) : 'action.hover',
                },
              }),
            }}
            onDragEnter={handleTemplateDrag}
            onDragLeave={handleTemplateDrag}
            onDragOver={handleTemplateDrag}
            onDrop={handleTemplateDrop}
            onClick={() => !uploadingTemplates && document.getElementById('template-file-input')?.click()}
          >
            <CloudUploadIcon sx={{
              fontSize: 40,
              color: templateDragActive
                ? (mode === 'dark' ? consoleColors.sand : 'primary.main')
                : (mode === 'dark' ? consoleColors.sandDim : 'text.secondary'),
              mb: 1,
            }} />
            <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>
              Drag and drop template files here, or click to select
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Multiple files supported · 50 MB max per file
            </Typography>
            <input
              id="template-file-input"
              type="file"
              hidden
              multiple
              disabled={uploadingTemplates}
              onChange={(e) => {
                handleAddTemplateFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </Paper>
        </CardContent>
      </Card>

      <Box display="flex" justifyContent="flex-end" mt={4}>
        <Button
          variant="outlined"
          size="large"
          onClick={handleSubmitProject}
          disabled={loading}
          sx={{
            height: 56,
            px: 4,
            borderRadius: 0,
            border: `2px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
            color: mode === 'dark' ? consoleColors.sand : theme.palette.primary.main,
            fontFamily: 'monospace',
            fontWeight: 700,
            fontSize: '1rem',
            letterSpacing: '0.1em',
            '&:hover': {
              backgroundColor: mode === 'dark' ? alpha(consoleColors.sand, 0.15) : alpha(theme.palette.primary.main, 0.08),
              border: `2px solid ${mode === 'dark' ? consoleColors.sand : theme.palette.primary.main}`,
            },
            '&:disabled': {
              borderColor: mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#ccc',
              color: mode === 'dark' ? alpha(consoleColors.sand, 0.3) : '#ccc'
            }
          }}
        >
          {loading ? (isUpdateMode ? 'PROCESSING...' : 'INITIALIZING...') : (isUpdateMode ? 'CONFIRM MODIFICATIONS' : 'DEPLOY PROJECT')}
        </Button>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectCreationPage;